import React, { useState } from 'react';
import { getFullApiUrl } from '../config';
import config from '../config';

const RnDUploadSection = ({ onProcessingComplete }) => {
  const [isDragOver, setIsDragOver] = useState(false);
  const [isProcessing, setIsProcessing] = useState(false);
  const [uploadStatus, setUploadStatus] = useState(null);
  const [processingSteps, setProcessingSteps] = useState([]);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const [customPrompt, setCustomPrompt] = useState('');

  const defaultPrompt = `Analyze the extracted text from the uploaded documents and provide a comprehensive summary.

Please organize your response with:
1. Document Overview
2. Key Information Extracted
3. Important Details
4. Summary and Insights

If there are tables or structured data, please format them clearly.`;

  const handleDragOver = (e) => {
    e.preventDefault();
    setIsDragOver(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    setIsDragOver(false);
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setIsDragOver(false);
    const files = Array.from(e.dataTransfer.files);
    if (files.length > 0) {
      setSelectedFiles(files);
    }
  };

  const handleFileSelect = (e) => {
    const files = Array.from(e.target.files);
    if (files.length > 0) {
      setSelectedFiles(files);
    }
  };

  const updateProcessingStep = (step, status = 'processing') => {
    setProcessingSteps(prev => {
      const updated = [...prev];
      const existingIndex = updated.findIndex(s => s.name === step);
      if (existingIndex >= 0) {
        updated[existingIndex].status = status;
      } else {
        updated.push({ name: step, status });
      }
      return updated;
    });
  };

  const handleProcess = async () => {
    if (selectedFiles.length === 0) {
      setUploadStatus({
        type: 'error',
        message: 'Please select files to process.'
      });
      return;
    }

    const promptToUse = customPrompt.trim() || defaultPrompt;

    // Validate file types
    const supportedTypes = ['.pdf', '.jpg', '.jpeg', '.png'];
    const invalidFiles = selectedFiles.filter(file => 
      !supportedTypes.some(type => file.name.toLowerCase().endsWith(type))
    );
    
    if (invalidFiles.length > 0) {
      setUploadStatus({
        type: 'error',
        message: `File type not supported: ${invalidFiles[0].name}. Please select PDF, JPG, JPEG, or PNG files.`
      });
      return;
    }

    // Validate file sizes
    const oversizedFiles = selectedFiles.filter(file => file.size > config.MAX_FILE_SIZE);
    if (oversizedFiles.length > 0) {
      setUploadStatus({
        type: 'error',
        message: `File size too large: ${oversizedFiles[0].name}. Maximum size is 50MB per file.`
      });
      return;
    }

    setIsProcessing(true);
    setProcessingSteps([]);
    setUploadStatus({
      type: 'info',
      message: `Starting RnD processing for ${selectedFiles.length} file(s)...`
    });

    try {
      const formData = new FormData();
      selectedFiles.forEach(file => {
        formData.append('files', file);
      });
      formData.append('prompt', promptToUse);

      // Show processing steps
      updateProcessingStep('File Upload', 'processing');
      updateProcessingStep('OCR Text Extraction', 'pending');
      updateProcessingStep('Gemini AI Processing', 'pending');
      updateProcessingStep('Custom Prompt Analysis', 'pending');
      updateProcessingStep('Response Generation', 'pending');

      const response = await fetch(getFullApiUrl('RND_PROMPTING'), {
        method: 'POST',
        body: formData,
      });

      const result = await response.json();

      if (response.ok && result.success) {
        updateProcessingStep('File Upload', 'completed');
        updateProcessingStep('OCR Text Extraction', 'completed');
        updateProcessingStep('Gemini AI Processing', 'completed');
        updateProcessingStep('Custom Prompt Analysis', 'completed');
        updateProcessingStep('Response Generation', 'completed');

        setUploadStatus({
          type: 'success',
          message: `Successfully processed ${result.files_processed} file(s) with custom prompt!`
        });

        if (onProcessingComplete) {
          onProcessingComplete(result);
        }
      } else {
        throw new Error(result.detail || result.message || 'Failed to process files with custom prompt');
      }
    } catch (error) {
      console.error('RnD processing error:', error);
      updateProcessingStep('File Upload', 'error');
      setUploadStatus({
        type: 'error',
        message: `Error: ${error.message}`
      });
    } finally {
      setIsProcessing(false);
    }
  };

  const getStepIcon = (status) => {
    switch (status) {
      case 'completed': return 'fa-check-circle text-green-500';
      case 'processing': return 'fa-spinner fa-spin text-blue-500';
      case 'error': return 'fa-exclamation-triangle text-red-500';
      default: return 'fa-clock text-gray-400';
    }
  };

  return (
    <section className="mb-8">
      {/* File Upload Section */}
      <div 
        className={`bg-white dark:bg-gray-800 rounded-lg border border-dashed p-8 text-center transition-colors mb-6 ${
          isDragOver 
            ? 'border-red-400 dark:border-red-500 bg-red-50 dark:bg-red-900/30' 
            : 'border-gray-300 dark:border-gray-600'
        }`}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onDrop={handleDrop}
      >
        <div className="max-w-md mx-auto">
          <div className="text-red-500 mb-4">
            <i className="fa-solid fa-flask text-4xl"></i>
          </div>
          <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-2 transition-colors duration-300">
            RnD Prompting Lab
          </h3>
          <p className="text-sm text-gray-500 dark:text-gray-300 mb-6 transition-colors duration-300">
            Upload documents (PDF, JPG, JPEG, PNG) and experiment with custom prompts
          </p>
          <label htmlFor="rnd-file-upload" className="cursor-pointer">
            <span className={`font-medium py-2.5 px-5 rounded-lg inline-flex items-center ${
              isProcessing 
                ? 'bg-gray-400 text-white cursor-not-allowed' 
                : 'bg-red-600 hover:bg-red-700 text-white'
            }`}>
              <i className="fa-solid fa-upload mr-2"></i>
              {selectedFiles.length > 0 ? `${selectedFiles.length} file(s) selected` : 'Select Files'}
            </span>
            <input
              id="rnd-file-upload"
              type="file"
              className="hidden"
              accept=".pdf,.jpg,.jpeg,.png"
              multiple
              onChange={handleFileSelect}
              disabled={isProcessing}
            />
          </label>
          
          <div className="mt-4 flex items-center justify-center text-xs text-gray-500 dark:text-gray-400 transition-colors duration-300">
            <i className="fa-solid fa-magic-wand-sparkles mr-2 text-red-500 dark:text-red-400"></i>
            <span>OCR + Gemini AI + Custom Prompting</span>
          </div>
        </div>
      </div>

      {/* Selected Files Display */}
      {selectedFiles.length > 0 && (
        <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-4 mb-6 transition-colors duration-300">
          <h4 className="text-sm font-medium text-gray-800 dark:text-white mb-3 transition-colors duration-300">Selected Files:</h4>
          <div className="space-y-2">
            {selectedFiles.map((file, index) => (
              <div key={index} className="flex items-center text-sm text-gray-600 dark:text-gray-300 transition-colors duration-300">
                <i className="fa-solid fa-file mr-2 text-gray-400 dark:text-gray-500"></i>
                <span>{file.name}</span>
                <span className="ml-auto text-xs text-gray-400 dark:text-gray-500 transition-colors duration-300">
                  {(file.size / 1024 / 1024).toFixed(2)} MB
                </span>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Custom Prompt Section */}
      <div className="bg-white dark:bg-gray-800 rounded-lg shadow-sm dark:shadow-gray-900/50 p-6 mb-6 transition-colors duration-300">
        <h4 className="text-lg font-medium text-gray-800 dark:text-white mb-4 flex items-center transition-colors duration-300">
          <i className="fa-solid fa-edit mr-2 text-red-600 dark:text-red-400"></i>
          Custom Prompt
        </h4>
        <textarea
          value={customPrompt}
          onChange={(e) => setCustomPrompt(e.target.value)}
          placeholder={defaultPrompt}
          className="w-full h-40 p-4 bg-white dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg resize-none text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-gray-400 focus:ring-2 focus:ring-red-500 focus:border-red-500 transition-colors duration-300"
          disabled={isProcessing}
        />
        <div className="mt-3 flex justify-between items-center">
          <div className="text-xs text-gray-500 dark:text-gray-400 transition-colors duration-300">
            {customPrompt.length > 0 ? `${customPrompt.length} characters` : 'Using default prompt'}
          </div>
          <button
            onClick={() => setCustomPrompt(defaultPrompt)}
            className="text-xs text-red-600 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 transition-colors duration-200"
            disabled={isProcessing}
          >
            Reset to Default
          </button>
        </div>
      </div>

      {/* Process Button */}
      <div className="text-center mb-6">
        <button
          onClick={handleProcess}
          disabled={isProcessing || selectedFiles.length === 0}
          className={`font-medium py-3 px-8 rounded-lg inline-flex items-center ${
            isProcessing || selectedFiles.length === 0
              ? 'bg-gray-400 text-white cursor-not-allowed' 
              : 'bg-red-600 hover:bg-red-700 text-white'
          }`}
        >
          <i className={`fa-solid ${isProcessing ? 'fa-spinner fa-spin' : 'fa-rocket'} mr-2`}></i>
          {isProcessing ? 'Processing...' : 'Process with Custom Prompt'}
        </button>
      </div>

      {/* Status Message */}
      {uploadStatus && (
        <div className={`mb-6 p-4 rounded-lg text-sm ${
          uploadStatus.type === 'success' ? 'bg-green-50 text-green-700' :
          uploadStatus.type === 'error' ? 'bg-red-50 text-red-700' :
          'bg-blue-50 text-blue-700'
        }`}>
          {uploadStatus.message}
        </div>
      )}

      {/* Processing Steps */}
      {processingSteps.length > 0 && (
        <div className="bg-white rounded-lg shadow-sm overflow-hidden">
          <div className="px-6 py-4 border-b border-gray-200">
            <h4 className="text-lg font-medium text-gray-800 flex items-center">
              <i className="fa-solid fa-cogs mr-2 text-red-600"></i>
              RnD Processing Pipeline
            </h4>
          </div>
          <div className="p-6">
            <div className="space-y-3">
              {processingSteps.map((step, index) => (
                <div key={index} className="flex items-center">
                  <i className={`fa-solid ${getStepIcon(step.status)} mr-3`}></i>
                  <span className={`text-sm ${
                    step.status === 'completed' ? 'text-green-700' :
                    step.status === 'processing' ? 'text-blue-700' :
                    step.status === 'error' ? 'text-red-700' :
                    'text-gray-500'
                  }`}>
                    {step.name}
                  </span>
                  {step.status === 'processing' && (
                    <span className="ml-2 text-xs text-blue-600 animate-pulse">
                      Processing...
                    </span>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
      )}
    </section>
  );
};

export default RnDUploadSection;
